import { useState, useEffect, useRef } from 'react'
import { Container, Col, Row, Badge } from 'reactstrap'
import { useSelector, useDispatch } from 'react-redux'
import { getAllAnalyzers } from '../../redux/slices'
import './Analyzers.css'
import ComponentBox from '../../components/ComponentBox/ComponentBox'
import { renderDateTime } from '../../utils/dateFormatter'
import 'devextreme/dist/css/dx.common.css'
import 'devextreme/dist/css/dx.light.css'
import ICSDataGrid from '../../components/ICSDataGrid/ICSDataGrid'
import { createEntityAdapter } from '@reduxjs/toolkit'
import {
  removeAnalyzer,
  removeAnalyzerResults,
  createJob,
  setEntityError,
} from '../../redux/slices'
import { DefaultState } from '../../types'
import HeaderBar from '../../components/HeaderBar/HeaderBar'
import AnalyzerGenerator from './AnalyzerGenerator/AnalyzerGenerator'
import { ICSDataCellDetailView } from '../../components/ICSDataCellDetailView/ICSDataCellDetailView'
import { capitalizeFirstLetter } from '../../utils/helpers'
import { ICSActionIcon } from '../../components'
import ICSActionButton from '../../components/ICS/ActionButton/ICSActionButton'
import ICSErrorCard from '../../components/ICSErrorCard/ICSErrorCard'
import { AppDispatch } from '../../services/lib'

const badgeColorMap = {
  status: {
    new: 'secondary',
    running: 'primary',
    complete: 'success',
    failed: 'danger'
  },
  dataSet: {
    static: 'dark',
    dynamic: 'warning',
  },
}

const LastStatusBadge = (data: { value: string }) => {
  return (
    <Badge color={badgeColorMap.status[data.value] || 'primary'} pill>
      {data.value && capitalizeFirstLetter(data.value)}
    </Badge>
  )
}

const DataSetBadge = (data: { value: string }) => {
  return (
    <Badge color='info' pill>
      {data.value && capitalizeFirstLetter(data.value)}
    </Badge>
  )
}

const Analyzers = () => {
  const dispatch: AppDispatch = useDispatch()
  const analyzersError = useSelector(
    (state: DefaultState) => state.analyzers.error
  )
  const currentUser = useSelector(
    (state: DefaultState) => state.session.data.user
  )
  const selectors = createEntityAdapter().getSelectors()
  const analyzersState = useSelector((state: DefaultState) => state.analyzers)
  const analyzersArray = selectors.selectAll(analyzersState)
  const [showGenerateModal, setShowGenerateModal] = useState<boolean>(false)
  const [showEditModal, setShowEditModal] = useState<boolean>(false)
  const [editAnalyzer, setEditAnalyzer] = useState(null)
  const interval: { current: NodeJS.Timeout | null } = useRef(null)

  useEffect(() => {
    const initialState = async () => {
      await dispatch(getAllAnalyzers())
    }

    if (showGenerateModal || showEditModal) {
      clearInterval(interval.current as NodeJS.Timeout)
    } else {
      interval.current = setInterval(async () => {
        // eslint-disable-next-line no-console
        initialState()
      }, 10000)
    }
  }, [dispatch, showGenerateModal, showEditModal])

  useEffect(() => {
    dispatch(getAllAnalyzers())
  }, [])

  const analyzerGenerateButtonClicked = () => {
    setShowGenerateModal(true)
  }
  const hideEditModal = () => {
    setShowEditModal(false)
    dispatch(
      setEntityError({
        sliceName: 'analyzers',
        entityName: 'Analyzer',
        error: null,
      })
    )
  }
  const hideModal = () => {
    setShowGenerateModal(false)
    dispatch(
      setEntityError({
        sliceName: 'analyzers',
        entityName: 'Analyzer',
        error: null,
      })
    )
  }
  const bypassHandler = e => {
    e.preventDefault()
  }
  const handleEdit = (_, data) => {
    if (data) {
      setEditAnalyzer(data)
      setShowEditModal(true)
    }
  }
  const handleRun = (_, data) => {
    if (data) {
      const r = window.confirm(
        `Are you sure you want to run analyzer (${data.name})?`
      )
      if (r === true) {
        dispatch(createJob(data.id))
      }
    }
  }
  const handleDelete = (_, data) => {
    if (data) {
      const r = window.confirm(
        `Are you sure you want to delete the following Analyzer: ${data.name}?`
      )
      if (r === true) {
        dispatch(removeAnalyzer(data.id))
      }
    }
  }
  const handleDeleteResults = (_, data) => {
    if (data) {
      const r = window.confirm(
        `Are you sure you want to delete ALL results currently assigned to Analyzer: ${data.name}`
      )
      if (r === true) {
        dispatch(removeAnalyzerResults(data.id))
      }
    }
  }
  const checkEnabled = (data: any) => {
    if (!data || !data.user || !data.user.id) return false
    const sameUser = currentUser?.id === data.user?.id
    const enabled =
      typeof data.last_status === 'string' && data.last_status === 'running'
        ? false
        : true
    return currentUser?.roles?.includes("admin") ? enabled : enabled && sameUser
  }

  return (
    <>
      <Container fluid={true}>
        <span>
          {showGenerateModal && (
            <AnalyzerGenerator
              hideModal={hideModal}
              location={false}
              showModal={showGenerateModal}
            />
          )}
        </span>
        <span>
          {showEditModal && (
            <AnalyzerGenerator
              hideModal={hideEditModal}
              location={{
                state: {
                  isEdit: true,
                  analyzer: editAnalyzer,
                },
              }}
              showModal={showEditModal}
            />
          )}
        </span>
        <Row>
          <Col className='wivi-col'>
            <HeaderBar
              origin='IPA'
              routes={[
                {
                  title: 'Analyzers',
                },
              ]}
              help={{ title: 'Analyzers', text: 'Analyzers.....' }}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={{ size: 12 }} className='wivi-col'>
            <ComponentBox>
              {analyzersError?.code === 'error' && !showGenerateModal ? (
                <ICSErrorCard
                  code={analyzersError.code}
                  message={analyzersError.message}
                  fields={analyzersError.fields}
                />
              ) : (
                <ICSDataGrid
                  dataSource={analyzersArray}
                  columnOptions={[
                    {
                      type: 'buttons',
                      caption: 'Action',
                      width: 110,
                      buttons: [
                        {
                          render: e => {
                            const { data } = e
                            return (
                              <ICSActionIcon
                                key={`action_edit_${data.id}`}
                                icon='edit'
                                title='Edit'
                                renderData={e}
                                onClick={
                                  checkEnabled(e.row.data)
                                    ? e => {
                                        handleEdit(e, data)
                                      }
                                    : bypassHandler
                                }
                                disabled={!checkEnabled(e.row.data)}
                              />
                            )
                          },
                        },
                        {
                          hint: 'run',
                          title: 'Run',
                          render: e => {
                            const { data } = e
                            return (
                              <ICSActionIcon
                                key={`action_run_${data.id}`}
                                icon='runner'
                                title='Run'
                                renderData={e}
                                onClick={
                                  checkEnabled(e.row.data)
                                    ? e => {
                                        handleRun(e, data)
                                      }
                                    : bypassHandler
                                }
                                disabled={!checkEnabled(e.row.data)}
                              />
                            )
                          },
                        },
                        {
                          icon: 'deletetable',
                          hint: 'Delete All Results',
                          render: e => {
                            const { data } = e
                            return (
                              <ICSActionIcon
                                key={`action_delete_results_${data.id}`}
                                icon='deletetable'
                                title='Delete All Results'
                                renderData={e}
                                onClick={e => {
                                  checkEnabled(data)
                                    ? handleDeleteResults(e, data)
                                    : bypassHandler(e)
                                }}
                                disabled={!checkEnabled(e.row.data)}
                              />
                            )
                          },
                        },
                        {
                          icon: 'trash',
                          hint: 'Delete',
                          render: e => {
                            const { data } = e
                            return (
                              <ICSActionIcon
                                key={`action_delete_${data.id}`}
                                icon='trash'
                                title='Delete'
                                renderData={e}
                                onClick={e => {
                                  checkEnabled(data)
                                    ? handleDelete(e, data)
                                    : bypassHandler(e)
                                }}
                                disabled={!checkEnabled(e.row.data)}
                              />
                            )
                          },
                        },
                      ],
                    },
                    { dataField: 'name', caption: 'Name' },
                    {
                      dataField: 'description',
                      caption: 'Description',
                      cssClass: 'td-dataset-description',
                      cellRender: data => {
                        return (
                          <ICSDataCellDetailView
                            {...data}
                            modalWidth='35vw'
                            modalHeight='20vh'
                          />
                        )
                      },
                    },
                    { dataField: 'type', caption: 'Type' },
                    {
                      dataField: 'script.name',
                      caption: 'Script Name',
                      cellRender: col => {
                        const scriptName = col.data.script.name
                        if (!scriptName) {
                          return (
                            <span style={{ color: 'red' }}>
                              Script is missing
                            </span>
                          )
                        }
                        return <span>{scriptName}</span>
                      },
                    },
                    {
                      dataField: 'configs[0].name',
                      caption: 'Configuration',
                    },
                    {
                      dataField: 'datasets[0].name',
                      caption: 'Data Sets',
                      cellRender: col => {
                        return (
                          <>
                            <DataSetBadge value={col.value} />
                            &nbsp;
                          </>
                        )
                      },
                    },
                    {
                      dataField: 'last_status',
                      caption: 'Last Status',
                      cellRender: col => {
                        const { last_status } = col.data
                        return (
                          <>
                            <LastStatusBadge value={last_status} />
                            &nbsp;
                          </>
                        )
                      },
                    },
                    {
                      dataField: 'user.name',
                      caption: 'User',
                    },
                    {
                      dataField: 'created_at',
                      caption: 'Date Created',
                      cellRender: renderDateTime,
                    },
                    {
                      dataField: 'updated_at',
                      caption: 'Last Updated',
                      cellRender: renderDateTime,
                    },
                  ]}
                  options={{
                    buttons: [
                      <ICSActionButton
                        id='analyzerGenerate'
                        type='success'
                        width={65}
                        height={35}
                        stylingMode='contained'
                        onClick={analyzerGenerateButtonClicked}
                        text='Add'
                      >
                        <span className='glyphicons glyphicons-floppy-disk'></span>
                        <span className='glyphicons glyphicons-plus'></span>
                      </ICSActionButton>,
                    ],
                    showScrollbar: 'always'
                  }}
                />
              )}
            </ComponentBox>
          </Col>
        </Row>
      </Container>
    </>
  )
}

export default Analyzers
